import { useParams } from "react-router-dom"
import classes from "./StudentJoin.module.scss"
import Header from "../../components/Header/Header"
import clsx from "clsx"
import blueLogo from "../../assets/blue-logo.svg"
import { useEffect, useMemo, useState } from "react"
import { BASE_URL, post } from "../../utils/requests"
import FormBuilder, {
  FieldsGroup,
} from "../../components/FormBuilder/FormBuilder"
import { formatReadablePhoneNumber } from "../../utils/phoneNumber"
import axios from "axios"

const fields: FieldsGroup[] = [
  {
    id: "personal",
    label: "פרטים אישיים",
    fields: [
      {
        id: "firstName",
        name: "firstName",
        label: "שם פרטי",
        type: "text",
        required: true,
      },
      {
        id: "lastName",
        name: "lastName",
        label: "שם משפחה",
        type: "text",
        required: true,
      },
      {
        id: "idNumber",
        name: "idNumber",
        label: "תעודת זהות",
        type: "text",
        required: true,
      },
      {
        id: "dateOfBirth",
        name: "dateOfBirth",
        label: "תאריך לידה",
        type: "date",
        required: true,
      },
    ],
  },
  {
    id: "contact",
    label: "פרטי יצירת קשר",
    fields: [
      {
        id: "phoneNumber",
        name: "phoneNumber",
        label: "מספר נייד",
        type: "tel",
        required: true,
      },
      {
        id: "email",
        name: "email",
        label: "כתובת אימייל",
        type: "email",
      },
    ],
  },
  {
    id: "address",
    label: "כתובת",
    fields: [
      {
        id: "city",
        name: "city",
        label: "עיר",
        type: "text",
        required: true,
      },
      {
        id: "street",
        name: "street",
        label: "רחוב",
        type: "text",
        required: true,
      },
      {
        id: "streetNumber",
        name: "streetNumber",
        label: "מספר בית",
        type: "text",
        required: true,
      },
    ],
  },
  {
    id: "medial",
    label: "פרטים רפואיים",
    fields: [
      {
        id: "hasGlasses",
        name: "hasGlasses",
        label: "מרכיב משקפיים?",
        type: "checkbox",
      },
      {
        id: "eyeExamDate",
        name: "eyeExamDate",
        label: "תאריך בדיקת עיניים",
        type: "date",
        required: false,
      },
      {
        id: "medicalExamDate",
        name: "medicalExamDate",
        label: "תאריך מילוי שאלון רפואי",
        type: "date",
        required: false,
      },
    ],
  },
  {
    id: "attachment",
    label: "מסמכים נדרשים",
    fields: [
      {
        id: "medicalDocument",
        name: "medicalDocument",
        label: "אישור בדיקת עיניים",
        type: "file",
        accepts: "image/*,.pdf",
        required: false,
      },
    ],
  },
  {
    id: "consents",
    label: "הצהרות תלמיד",
    fields: [
      {
        id: "filledFormOnline",
        name: "filledFormOnline",
        label:
          "אני מאשר/ת שמילאת טופס מקוון באתר משרד הרישוי ואני רשאי ללמוד נהיגה",
        type: "checkbox",
        required: true,
      },
      {
        id: "agreedToAge",
        name: "agreedToAge",
        label: "אני מאשר/ת שאני בגיל המתאים ללימוד נהיגה",
        type: "checkbox",
        required: true,
      },
    ],
  },
]

export const isValidIdNumber = (idNumber: string) => {
  // Copied from here: https://www.upnext.co.il/articles/israeli-id-numer-validation/

  let id = String(idNumber).trim()
  if (id.length > 9 || isNaN(Number(id))) {
    return false
  }
  id = id.length < 9 ? ("00000000" + id).slice(-9) : id

  return (
    Array.from(id, Number).reduce((counter, digit, i) => {
      const step = digit * ((i % 2) + 1)
      return counter + (step > 9 ? step - 9 : step)
    }) %
      10 ===
    0
  )
}

const StudentJoin = () => {
  const [teacherDetails, setTeacherDetails] = useState<
    TeacherDetails & { phoneNumber: string }
  >()
  const [noTeacherFound, setNoTeacherFound] = useState(false)
  const [formDone, setFormDone] = useState(false)
  const [loading, setLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const params = useParams<"teacherUID">()
  let teacherUID
  try {
    teacherUID = Number(atob(params.teacherUID))
  } catch {}

  const [values, setValues] = useState<object>({
    hasGlasses: false,
    teacherUID,
  })
  const [loadingStatus, setLoadingStatus] = useState<string>("")

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    const getDetails = async () => {
      try {
        const data = await post(`${BASE_URL}/teachers/details`, { teacherUID })

        if (data.details) {
          setTeacherDetails(data.details)
          setLoading(false)
        } else {
          setLoading(false)
          setNoTeacherFound(true)
        }
      } catch {
        setHasError(true)
      }
    }

    getDetails()
  }, [teacherUID])

  const teacherFullName = useMemo(
    () =>
      [teacherDetails?.firstName, teacherDetails?.lastName]
        .filter(Boolean)
        .join(" "),
    [teacherDetails],
  )

  const uploadAFile = async (
    type: "idPhoto" | "medicalDocument",
    studentUID: number,
  ) => {
    const formData = new FormData()
    formData.append("file", values[`${type}File`])
    formData.append("path", values[type])

    try {
      await axios.post(`${BASE_URL}/students/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Student-UID": studentUID,
          "X-File": type, // or "medicalDocument" or any other
        },
      })
    } catch {}
  }

  const checkRequiredFields = () => {
    const requiredFields = fields
      .map((field) => field.fields.filter((f) => f.required).map((f) => f.name))
      .flat()

    const missingFields: string[] = []

    requiredFields.forEach((field) => {
      if (!values[field].toString().trim()) {
        missingFields.push(field)
      }
    })

    return missingFields
  }

  const scrollToField = (fieldName: string) => {
    console.log(fieldName)
    let inputName = fieldName
    if (fieldName === "general") {
      inputName = "submit-form"
    }
    const field = document.querySelector(`#${inputName}`)
    if (field) {
      field.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }
  const onSubmit = async (values: object) => {
    const missingFields = checkRequiredFields()

    const errors: Record<string, string> = {}
    missingFields.forEach((field) => (errors[field] = "שדה חובה"))
    if (!isValidIdNumber(values["idNumber"])) {
      errors["idNumber"] = "יש להזין תעודת זהות תקינה"
    }
    setErrors(errors)

    if (Object.keys(errors).length > 0) {
      const fieldName = missingFields[0]
      scrollToField(fieldName)
      return
    }
    setLoadingStatus("שולח את הבקשה")
    const data = await post(`${BASE_URL}/students/join`, values)

    if (data?.error) {
      if (!!data.error.field) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [data.error.field]: data.error.message,
        }))
        scrollToField(data.error.field)
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          general: data.error.message,
        }))
      }
      setLoadingStatus("")
      return
    }

    if (!data?.studentDetails) {
      setLoadingStatus("")
      return
    }

    if (values["idPhoto"] && values["idPhoto"]) {
      setLoadingStatus("מעלה את צילום תעודת הזהות")
      await uploadAFile("idPhoto", data.studentDetails.uid)
    }
    if (values["medicalDocumentFile"] && values["medicalDocument"]) {
      setLoadingStatus("מעלה את השאלון הרפואי")
      await uploadAFile("medicalDocument", data.studentDetails.uid)
    }

    setLoadingStatus("")

    setFormDone(true)
  }

  if (hasError) {
    return (
      <div className={classes.generalMessage}>
        <a
          className={classes.logoWrapper}
          href="https://diodrive.co.il"
          target="_blank"
          rel="noreferrer"
        >
          <img src={blueLogo} className={classes.logo} alt="" />
        </a>
        <h2>נתקלנו בתקלה</h2>
        <p>לא הצלחנו לטעון את הטופס שביקשת, אנא נסה שוב</p>
      </div>
    )
  }
  if (loading) {
    return (
      <div className={classes.generalMessage}>
        <a
          className={classes.logoWrapper}
          href="https://diodrive.co.il"
          target="_blank"
          rel="noreferrer"
        >
          <img src={blueLogo} className={classes.logo} alt="" />
        </a>
        <h2>טוען את הטופס המבוקש</h2>
        <p>אנחנו טוענים בשבילך את הטופס שביקשת</p>
      </div>
    )
  }

  if (formDone) {
    return (
      <div className={classes.generalMessage}>
        <a
          className={classes.logoWrapper}
          href="https://diodrive.co.il"
          target="_blank"
          rel="noreferrer"
        >
          <img src={blueLogo} className={classes.logo} alt="" />
        </a>
        <h2>✅ בקשתך נשלחה</h2>
        <p>
          תודה רבה על מילוי השאלון, בקשתך נשלחה ל{teacherFullName} להמשך טיפול
        </p>
      </div>
    )
  }

  if (noTeacherFound || !teacherUID) {
    return (
      <div className={classes.generalMessage}>
        <a
          className={classes.logoWrapper}
          href="https://diodrive.co.il"
          target="_blank"
          rel="noreferrer"
        >
          <img src={blueLogo} className={classes.logo} alt="" />
        </a>
        <h2>הטופס המבוקש לא נמצא</h2>
        <p>לא מצאנו את הטופס שאתם מחפשים</p>
      </div>
    )
  }
  return (
    <>
      {loadingStatus && <div className={classes.loading}>{loadingStatus}</div>}
      <div className={classes.root}>
        <div className={clsx(classes.header, "padded")}>
          <a
            className={classes.logoWrapper}
            href="https://diodrive.co.il"
            target="_blank"
            rel="noreferrer"
          >
            <img src={blueLogo} className={classes.logo} alt="" />
            <Header
              title={teacherFullName}
              subtitle={`מלאו את הטופס כדי לשלוח בקשת הצטרפות ללימוד נהיגה אצל ${teacherFullName}`}
            />
          </a>
        </div>
        <div className={clsx(classes.teacher, "padded")}>
          {teacherDetails?.phoneNumber && (
            <div className={classes.teacherDetail}>
              <h3>מספר נייד ליצירת קשר</h3>
              <a href={`tel:${teacherDetails.phoneNumber}`}>
                {formatReadablePhoneNumber(teacherDetails.phoneNumber)}
              </a>
            </div>
          )}
        </div>
        <div className={clsx(classes.content, "padded")}>
          <FormBuilder
            groups={fields}
            values={values}
            onChange={setValues}
            onSubmit={onSubmit}
            withCaptcha={false}
            errors={errors}
            onErrorsChange={setErrors}
          />
        </div>
      </div>
    </>
  )
}

export default StudentJoin
